/**
 * MediaPreview
 *
 * Renders downloaded media files inline:
 *   - Single image: full-width with click-to-lightbox
 *   - Gallery (multi-image): hero image + thumbnail strip with navigation
 *   - Video: native <video> player with controls
 *   - Mixed: images first, then videos
 *
 * Replaces the plain file list in DownloadSection for completed downloads.
 */

import { ChevronLeft, ChevronRight, Download, Play } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import type { DownloadFile } from '@/hooks/useDownloads';
import { formatBytes } from '@/hooks/useDownloads';
import { MediaLightbox } from '@/components/MediaLightbox';
import { cn } from '@/lib/utils';

interface MediaPreviewProps {
	files: DownloadFile[];
	linkId: number;
}

function isImageFile(f: DownloadFile): boolean {
	return f.mimeType.startsWith('image/') && !f.filename.endsWith('.json');
}

function isVideoFile(f: DownloadFile): boolean {
	return f.mimeType.startsWith('video/');
}

function handleSaveFile(file: DownloadFile) {
	const a = document.createElement('a');
	a.href = file.url;
	a.download = file.filename;
	a.click();
}

/**
 * Single image or gallery hero + thumbnail strip
 */
const ImageGallery = memo(function ImageGallery({
	images,
	allMediaFiles,
}: {
	images: DownloadFile[];
	allMediaFiles: DownloadFile[];
}) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(0);
	const hero = images[activeIndex];
	const hasMultiple = images.length > 1;

	const goNext = useCallback(() => {
		setActiveIndex((i) => (i + 1) % images.length);
	}, [images.length]);

	const goPrev = useCallback(() => {
		setActiveIndex((i) => (i - 1 + images.length) % images.length);
	}, [images.length]);

	function openLightbox(index: number) {
		// Map image index to allMediaFiles index
		const file = images[index];
		const globalIndex = allMediaFiles.indexOf(file);
		setLightboxIndex(globalIndex >= 0 ? globalIndex : 0);
		setLightboxOpen(true);
	}

	return (
		<div className="space-y-2">
			{/* Hero image */}
			<div className="relative group rounded-lg overflow-hidden bg-black/5">
				<img
					src={hero.url}
					alt={hero.filename}
					className="w-full max-h-[60vh] object-contain cursor-pointer"
					onClick={() => openLightbox(activeIndex)}
					draggable={false}
				/>

				{/* Navigation arrows */}
				{hasMultiple && (
					<>
						<button
							type="button"
							onClick={(e) => { e.stopPropagation(); goPrev(); }}
							className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
						>
							<ChevronLeft className="size-5" />
						</button>
						<button
							type="button"
							onClick={(e) => { e.stopPropagation(); goNext(); }}
							className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
						>
							<ChevronRight className="size-5" />
						</button>
					</>
				)}

				{/* Counter badge */}
				{hasMultiple && (
					<div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs font-mono rounded">
						{activeIndex + 1} / {images.length}
					</div>
				)}

				{/* Download overlay */}
				<button
					type="button"
					onClick={(e) => { e.stopPropagation(); handleSaveFile(hero); }}
					className="absolute bottom-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
					title={`Download ${hero.filename} (${formatBytes(hero.sizeBytes)})`}
				>
					<Download className="size-4" />
				</button>
			</div>

			{/* Thumbnail strip */}
			{hasMultiple && (
				<div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
					{images.map((img, i) => (
						<button
							key={img.filename}
							type="button"
							onClick={() => setActiveIndex(i)}
							className={cn(
								'shrink-0 rounded overflow-hidden border-2 transition-all',
								i === activeIndex
									? 'border-primary ring-1 ring-primary/50'
									: 'border-transparent opacity-60 hover:opacity-100'
							)}
						>
							<img
								src={img.url}
								alt={img.filename}
								className="h-14 w-14 object-cover"
								loading="lazy"
							/>
						</button>
					))}
				</div>
			)}

			{/* Lightbox */}
			{lightboxOpen && (
				<MediaLightbox
					files={allMediaFiles}
					initialIndex={lightboxIndex}
					onClose={() => setLightboxOpen(false)}
				/>
			)}
		</div>
	);
});

/**
 * Video player with controls
 */
const VideoPlayer = memo(function VideoPlayer({
	file,
	posterUrl,
}: {
	file: DownloadFile;
	posterUrl?: string;
}) {
	return (
		<div className="space-y-2">
			<div className="relative rounded-lg overflow-hidden bg-black">
				<video
					src={file.url}
					controls
					preload="metadata"
					poster={posterUrl}
					className="w-full max-h-[60vh]"
				>
					<track kind="captions" />
				</video>
			</div>
			<div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
				<span>{file.filename}</span>
				<button
					type="button"
					onClick={() => handleSaveFile(file)}
					className="flex items-center gap-1 hover:text-primary transition-colors"
				>
					<Download className="size-3" />
					{formatBytes(file.sizeBytes)}
				</button>
			</div>
		</div>
	);
});

/**
 * Video thumbnail card (for multiple videos)
 */
function VideoCard({
	file,
	onClick,
}: {
	file: DownloadFile;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="relative rounded-lg overflow-hidden bg-black/10 hover:ring-2 ring-primary transition-all group"
		>
			<video
				src={file.url}
				preload="metadata"
				className="w-full h-32 object-cover"
			/>
			<div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
				<Play className="size-8 text-white" fill="white" />
			</div>
			<div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px] font-mono rounded">
				{formatBytes(file.sizeBytes)}
			</div>
		</button>
	);
}

export const MediaPreview = memo(function MediaPreview({ files, linkId: _linkId }: MediaPreviewProps) {
	const [activeVideo, setActiveVideo] = useState<DownloadFile | null>(null);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(0);

	const images = useMemo(() => files.filter(isImageFile), [files]);
	const videos = useMemo(() => files.filter(isVideoFile), [files]);
	const allMedia = useMemo(() => [...images, ...videos], [images, videos]);

	// Nothing viewable
	if (images.length === 0 && videos.length === 0) return null;

	// Single video, no images
	if (images.length === 0 && videos.length === 1) {
		return <VideoPlayer file={videos[0]} />;
	}

	// Multiple videos, no images
	if (images.length === 0 && videos.length > 1) {
		return (
			<div className="space-y-2">
				{activeVideo ? (
					<VideoPlayer file={activeVideo} />
				) : (
					<div className="grid grid-cols-2 gap-2">
						{videos.map((v) => (
							<VideoCard key={v.filename} file={v} onClick={() => setActiveVideo(v)} />
						))}
					</div>
				)}
				{activeVideo && (
					<button
						type="button"
						onClick={() => setActiveVideo(null)}
						className="text-xs text-muted-foreground hover:text-primary font-mono"
					>
						Show all {videos.length} videos
					</button>
				)}
			</div>
		);
	}

	// Images (possibly with videos below)
	return (
		<div className="space-y-3">
			<ImageGallery images={images} allMediaFiles={allMedia} />

			{/* Videos section below images */}
			{videos.length > 0 && (
				<div className="space-y-2">
					<div className="text-xs text-muted-foreground font-mono">
						{videos.length} video{videos.length > 1 ? 's' : ''}
					</div>
					{videos.length === 1 ? (
						<VideoPlayer file={videos[0]} />
					) : (
						<div className="grid grid-cols-2 gap-2">
							{videos.map((v) => (
								<VideoCard
									key={v.filename}
									file={v}
									onClick={() => {
										const globalIndex = allMedia.indexOf(v);
										setLightboxIndex(globalIndex >= 0 ? globalIndex : 0);
										setLightboxOpen(true);
									}}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{lightboxOpen && (
				<MediaLightbox
					files={allMedia}
					initialIndex={lightboxIndex}
					onClose={() => setLightboxOpen(false)}
				/>
			)}
		</div>
	);
});
